import { Link } from 'react-router-dom';
import { Paragraph, TrustpilotStandardWidget as TrustpilotWidget, Skeleton, Button } from '@shopmuchmore/design-system';
import AppLogo from './AppLogo';
import { env } from '../config/env';
import { LuInstagram } from "react-icons/lu";
import { BsTwitterX } from "react-icons/bs";
import { FaTiktok, FaFacebookF, FaLinkedinIn } from "react-icons/fa";
import { Smartphone, Download } from 'lucide-react';
import { useProductCategoriesQuery } from '../features/product/product.services';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';

import scotlandIsLogo from '../assets/quality-assurance-logos/scotlandis.webp';


// Footer Link Data
const footerLinks = {
  users: {
    title: 'Users',
    links: [
      { label: 'My Account', href: '/account' },
      { label: 'Order History', href: '/account/orders' },
      { label: 'Shopping Cart', href: '/cart' },
    ],
  },
  joinCompany: {
    title: 'Join the company',
    links: [
      { label: 'Sell on ShopMuchMore', href: `${env.merchantPlatformUrl}manual` },
      { label: 'ShopMuchMore Logistics', href: '/logistics' },
      { label: 'Become a Partner', href: '/partner' },
    ],
  },
  company: {
    title: 'Company',
    links: [
      { label: 'About Us', href: '/about' },
      { label: 'Terms & Conditions', href: '/terms-and-conditions' },
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Return & Cancellation Policy', href: '/return-and-cancellation-policy' },
    ],
  },
  categories: {
    title: 'Categories',
    links: [
      { label: 'Fresh Produce', href: '/browse?slug=fresh-produce' },
      { label: 'Dairy Products', href: '/browse?slug=dairy' },
      { label: 'African Textile', href: '/browse?slug=african-textile' },
      { label: 'Gadgets', href: '/browse?slug=gadgets' },
      { label: 'Home Appliances', href: '/browse?slug=home-appliances' },
    ],
  },
  usefulLinks: {
    title: 'Useful Links',
    links: [
      { label: 'Merchant Manual', href: '/merchant-manual' },
      { label: 'Help Center', href: '/contact' },
      { label: 'Data Deletion Instructions', href: '/data-deletion' },
      { label: 'Install App', href: '#install-app', isAction: true },
    ],
  },
};

// Social Links - filtered to only show configured ones
const socialLinks = [
  { name: 'LinkedIn', href: env.social.linkedin, Icon: FaLinkedinIn },
  { name: 'Instagram', href: env.social.instagram, Icon: LuInstagram },
  { name: 'X', href: env.social.x, Icon: BsTwitterX },
  { name: 'Tiktok', href: env.social.tiktok, Icon: FaTiktok },
  { name: 'Facebook', href: env.social.facebook, Icon: FaFacebookF },
].filter((link) => link.href);

function FooterLinkSection({ title, links }: { title: string; links: { label: string; href: string; isAction?: boolean }[] }) {
  return (
    <div>
      <h3 className="text-base font-semibold text-gray-900 mb-4">{title}</h3>
      <ul className="space-y-3">
        {links.map((link) => {
          if (link.isAction) {
            return (
              <li key={link.label}>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    if (link.href === '#install-app') {
                      window.dispatchEvent(new Event('show-pwa-install'));
                    }
                  }}
                  className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-primary transition-colors text-left cursor-pointer group"
                >
                  <Smartphone className="w-4 h-4 text-primary group-hover:scale-110 transition-transform" />
                  <span>{link.label}</span>
                </button>
              </li>
            );
          }

          const isExternal = link.href.startsWith('http');
          return (
            <li key={link.href}>
              {isExternal ? (
                <a
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-gray-600 hover:text-primary transition-colors"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  to={link.href}
                  className="text-sm text-gray-600 hover:text-primary transition-colors"
                >
                  {link.label}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function AppFooter() {
  const currentYear = new Date().getFullYear();
  const [ref, inView] = useIntersectionObserver({ triggerOnce: true, rootMargin: '400px' });
  const { data: categories, isLoading: isCategoriesLoading } = useProductCategoriesQuery({
    enabled: inView
  });

  const dynamicCategories = categories?.slice(0, 5).map(cat => ({
    label: cat.name,
    href: `/browse?slug=${cat.slug}`
  })) || footerLinks.categories.links;

  return (
    <footer ref={ref as any} className="bg-white border-t border-gray-200">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12">
        {/* Top Section - Logo, Address, Contact, Social & App Install */}
        <div className="mb-12">
          {/* Logo */}
          <AppLogo/>

          {/* Address */}
          <Paragraph className="text-gray-600 mb-3">
            Shop Much More, Third floor, 3 Hill Street, New Town, Edinburgh, EH2 3JP
          </Paragraph>

          {/* Contact Info */}
          <div className="space-y-1 text-sm text-gray-600 mb-6">
            <div>
              <span className="text-gray-900">Phone number:</span>{' '}
              <a href="tel:+447770041335" className="text-primary hover:underline">
                +44 7770041335
              </a>
            </div>
            <div>
              <span className="text-gray-900">Email:</span>{' '}
              <a href={`mailto:${env.supportEmail}`} className="text-primary hover:underline">
                {env.supportEmail}
              </a>
            </div>
          </div>

          {/* Social Media */}
          <div className="pt-6 border-t border-gray-100">
            <Paragraph size="sm" className="text-gray-600 mb-3 font-medium">
              Follow us on social media:
            </Paragraph>
            <div className="flex gap-2 mb-6">
              {socialLinks.map(({ name, href, Icon }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center hover:bg-primary transition-colors"
                  aria-label={`Follow us on ${name}`}
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>

            {/* Install Web App CTA Banner */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gray-50 border border-gray-200/80 rounded-2xl p-4 md:px-5 max-w-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Smartphone className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-gray-900">Install ShopMuchMore App</h4>
                  <p className="text-xs text-gray-500">Fast, convenient access directly from your home screen</p>
                </div>
              </div>
              <Button
                variant="default"
                size="sm"
                onClick={() => window.dispatchEvent(new Event('show-pwa-install'))}
                className="w-full sm:w-auto text-xs px-4 py-2 font-semibold rounded-xl shrink-0 gap-1.5"
              >
                <Download className="w-3.5 h-3.5" />
                Install App
              </Button>
            </div>
          </div>
        </div>

        {/* Link Sections */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 mb-12">
          <FooterLinkSection {...footerLinks.users} />
          <FooterLinkSection {...footerLinks.joinCompany} />
          <FooterLinkSection {...footerLinks.company} />
          {isCategoriesLoading ? (
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-gray-900">Categories</h3>
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-4 w-24" />
                ))}
              </div>
            </div>
          ) : (
            <FooterLinkSection title="Categories" links={dynamicCategories} />
          )}
          <FooterLinkSection {...footerLinks.usefulLinks} />
        </div>

        {/* Bottom Section - Payment Methods & Trust Badges */}
        <div className="pt-8 border-t border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Payment Methods */}
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider mr-1">
                Payment Methods
              </span>
              {/* Apple Pay */}
              <div className="h-8 px-3 bg-black rounded-md flex items-center justify-center shadow-xs">
                <span className="text-white text-xs font-medium">Apple Pay</span>
              </div>
              {/* Google Pay */}
              <div className="h-8 px-3 bg-white border border-gray-200 rounded-md flex items-center justify-center shadow-xs">
                <span className="text-gray-700 text-xs font-medium">G Pay</span>
              </div>
              {/* Visa */}
              <div className="h-8 px-3 bg-white border border-gray-200 rounded-md flex items-center justify-center shadow-xs">
                <span className="text-blue-800 text-xs font-bold italic">VISA</span>
              </div>
              {/* Mastercard */}
              <div className="h-8 w-12 bg-white border border-gray-200 rounded-md flex items-center justify-center shadow-xs">
                <div className="flex">
                  <div className="w-4 h-4 bg-red-500 rounded-full" />
                  <div className="w-4 h-4 bg-yellow-500 rounded-full -ml-2" />
                </div>
              </div>
              {/* Secure Payment */}
              <div className="h-8 px-3 bg-white border border-gray-200 rounded-md flex items-center gap-1.5 shadow-xs">
                <svg className="w-4 h-4 text-green-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="text-xs text-gray-600 font-medium">Secure Payment</span>
              </div>
            </div>

            {/* Quality Assurance & Trust Badges */}
            <div className="flex flex-wrap items-center gap-4 lg:justify-end">
              {/* SSL Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-md bg-gray-50 h-9 w-fit shadow-xs">
                <svg className="w-4 h-4 text-primary shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="text-xs text-gray-600">
                  Secured by <span className="font-semibold text-primary">positiveSSL</span>
                </span>
              </div>

              {/* Quality Assurance Logos */}
              <img src={scotlandIsLogo} alt="ScotlandIS Member" className="h-9 w-auto max-w-[140px] object-contain rounded" width="140" height="36" />
              <TrustpilotWidget alignment="center" className="min-h-[44px] min-w-[120px]" />
            </div>
          </div>
        </div>
      </div>

      {/* Copyright Bar */}
      <div className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4">
          <Paragraph size="sm" className="text-gray-500 text-center">
            ShopMuchMore eCommerce &copy; {currentYear}. All Rights Reserved
          </Paragraph>
        </div>
      </div>
    </footer>
  );
}

export default AppFooter;
